import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { PlayerEvents, TrailerPlayerAssetInfo } from 'types/Player';
import GSApi from 'api/Api';
import { usePlayer } from 'providers/player/PlayerContext';
import { PageErrorNotFound } from 'components/Pages/PageErrorNotFound/PageErrorNotFound';
import { PlayerWrapper } from 'components/Player/PlayerWrapper';
import { useNavigation } from 'hooks/useNavigation';
import { useAuth } from 'providers/useAuth/AuthContext';
import { FetchParamById, StreamIDKey } from 'types/ApiTypes';
import { useGlobalNetworkError, useGlobalNoInternet, withNetworkCheck } from 'hooks/withNetworkCheck/withNetworkCheck';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { getPlayerPageHelmetData } from 'utils/fnHelmet';
import { getErrorStatus } from 'utils/fnError';
import { withPlayer } from 'hooks/withPlayer/withPlayer';
import CastHandler from 'providers/cast/CastHandler';
import PlaybackManager from 'providers/player/PlaybackManager';
import PageTakeover from 'components/Pages/Settings/PageTakeover/PageTakeover';
import { guardedLocalStorage } from 'utils/fnStorage';

const TrailerPlayerPage: FC<{ assetId: string }> = ({ assetId }) => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { assetId: assetIdFromUrl } = useParams<{ assetId: string }>();
    const [currentAssetId, setCurrentAssetId] = useState(assetIdFromUrl || assetId);

    const { response, error, loading, fetcher, reset } = useDataFetcher<any, FetchParamById>(
        (params) => {
            setCurrentAssetId(assetIdFromUrl || assetId);
            return GSApi.createStreamSession('VOD_TRAILER', StreamIDKey.VOD_ASSET_ID, params.id || assetIdFromUrl, {}, (res) => res);
        },
        onNoInternet,
        onNetworkError
    );

    const { isGuest, openGuestAlert, isSignedOutNotice } = useAuth();
    const { asset, setAsset, addEventListener, removeEventListener, resetPlayerAsset, isMini, closeMiniPlayer } = usePlayer();
    const { onBackPressed } = useNavigation();

    const fetchDetails = () => {
        if (!isGuest && !asset && !CastHandler.isCast()) {
            fetcher({ id: currentAssetId });
        }
    };

    const onStreamEndReached = async () => {
        if (isMini) {
            closeMiniPlayer();
        } else {
            onBackPressed();
        }
    };

    const movieDetails = JSON.parse(guardedLocalStorage.getItem('movieDetails'));

    useEffect(() => {
        if (response) {
            const assetToSet: TrailerPlayerAssetInfo = {
                ...response,
                type: 'trailer',
                id: movieDetails.id,
                title: movieDetails.title,
                image: movieDetails.background_image_url || movieDetails.image,
                genres: movieDetails.genres,
                releaseYear: movieDetails.release_year,
                countries: movieDetails.production_countries,
                cast: movieDetails.cast,
                captionLanguages: movieDetails.subtitle_languages,
                originalLanguages: movieDetails.audio_languages,
                description: movieDetails.description,
                duration: movieDetails.duration,
            };

            const localKey = 'el-str-id';
            if (guardedLocalStorage.getItem(localKey)) {
                GSApi.endStreamSession(guardedLocalStorage.getItem(localKey));
            }
            guardedLocalStorage.setItem(localKey, movieDetails.id);
            PlaybackManager.startHeartbeat(response.id, response.heartbeat_interval);

            setAsset(assetToSet);
        }
    }, [response]);

    useEffect(() => {
        resetPlayerAsset();

        if (CastHandler.isCast()) {
            const castAsset = PlaybackManager.getPlayingAsset();
            if (castAsset) {
                setAsset(castAsset);
            }
        }

        fetchDetails();
        addEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);
        return () => {
            removeEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);
        };
    }, [currentAssetId]);

    useEffect(() => {
        if (isGuest) {
            openGuestAlert('/');
        }
    }, []);

    useEffect(() => {
        addEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);

        return () => {
            removeEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);
        };
    }, [response, isMini]);

    if (error && !isSignedOutNotice) {
        if (getErrorStatus(error) === 409) {
            return (
                <PageTakeover
                    limitReached
                    onSessionDeleted={() => {
                        reset();
                        fetchDetails();
                    }}
                />
            );
        }

        return <PageErrorNotFound onButtonClick={closeMiniPlayer} />;
    }

    return (
        <>
            {response && <HelmetWrapper {...getPlayerPageHelmetData(movieDetails)} />}
            <PlayerWrapper loading={loading} />
        </>
    );
};

export default withNetworkCheck(withPlayer(TrailerPlayerPage));
